/**
 * Optional, explicit, consent-gated local capture of this device's leg (stereo: channel 0 is the
 * local microphone as sent, channel 1 the remote audio as received). The PBX recorder is the
 * system of record. A local capture is never authoritative and never complete: it misses
 * everything outside this device leg (transfers, consult legs, conference participants joined
 * elsewhere), and the manifest says so. Persistence, encryption and upload are not implemented;
 * chunks go only to the caller's in-memory listener.
 *
 * Availability is gated by the server capability `desktop.capture`, which B1 reports as
 * `unimplemented` today. Until the backend enables it, `availability()` returns `unsupported`.
 */
import type { Capability } from '@bcio-voice/contracts';
import type { MediaStreamLike } from './platform.js';
import type { Disposer } from './result.js';
import type { Scheduler } from './scheduler.js';

export const CAPTURE_CAPABILITY = 'desktop.capture';
const CONSENT_MAX_AGE_MS = 10 * 60_000;

export interface LocalCaptureConsent {
  /** The user pressed an explicit capture control after seeing the notice. */
  readonly confirmed_by_user: true;
  /** The other parties were told (announcement or verbal notice). The app cannot verify this; the user attests it. */
  readonly other_parties_notified: true;
  readonly notice_version: string;
  readonly confirmed_at: string;
}

export type CaptureUnsupportedReason =
  | 'capability_unimplemented'
  | 'capability_unconfigured'
  | 'capability_disabled'
  | 'capability_degraded'
  | 'capability_not_allowed'
  | 'capability_unknown'
  | 'no_backend'
  | 'platform_unsupported';

export type CaptureAvailability =
  | { readonly status: 'unsupported'; readonly reason: CaptureUnsupportedReason }
  | { readonly status: 'unavailable'; readonly reason: 'call_not_found' | 'call_not_established' | 'no_remote_audio' | 'microphone_lost' }
  | { readonly status: 'available' }
  | { readonly status: 'active' };

export interface CaptureStartInput {
  readonly local: MediaStreamLike;
  readonly remote: MediaStreamLike;
  readonly timesliceMs: number;
  onData(data: Blob): void;
  onError(code: string): void;
}

export interface CaptureRecorder {
  readonly mimeType: string;
  replaceLocal(stream: MediaStreamLike): void;
  stop(): Promise<void>;
}

export interface CaptureBackend {
  readonly supported: boolean;
  start(input: CaptureStartInput): Promise<CaptureRecorder>;
}

export type CaptureIncompleteReason =
  | 'not_system_of_record'
  | 'started_after_answer'
  | 'participants_changed'
  | 'muted_intervals'
  | 'held_intervals'
  | 'media_lost_intervals'
  | 'input_device_switched'
  | 'size_limit_reached'
  | 'duration_limit_reached'
  | 'recorder_error';

export interface CaptureInterval {
  readonly kind: 'muted' | 'held' | 'media_lost';
  readonly start: string;
  readonly end: string | null;
}

export interface CaptureManifest {
  readonly schema: 'bcio.local_capture_manifest';
  readonly version: 1;
  readonly call_id: string;
  readonly authoritative: false;
  readonly system_of_record: 'pbx';
  readonly complete: false;
  readonly scope: 'this_device_leg';
  readonly channels: readonly [
    { readonly index: 0; readonly source: 'local_microphone_as_sent' },
    { readonly index: 1; readonly source: 'remote_audio_as_received'; readonly speakers: 'mixed_unattributed' },
  ];
  readonly mime_type: string;
  readonly persistence: 'caller_memory_only';
  readonly encryption: 'none';
  readonly upload: 'unsupported';
  readonly consent: LocalCaptureConsent;
  readonly started_at: string;
  readonly ended_at: string | null;
  readonly bytes: number;
  readonly chunks: number;
  readonly incomplete_reasons: readonly CaptureIncompleteReason[];
  readonly intervals: readonly CaptureInterval[];
  readonly participant_changes: number;
  readonly input_switches: number;
  readonly stopped_reason: string | null;
}

export interface LocalCaptureHandle {
  readonly call_id: string;
  readonly state: 'recording' | 'stopped';
  onData(listener: (data: Blob, sequence: number) => void): Disposer;
  manifest(): CaptureManifest;
  stop(): Promise<CaptureManifest>;
}

export function captureCapabilityStatus(capability: Capability | undefined): { ok: true } | { ok: false; reason: CaptureUnsupportedReason } {
  if (!capability) return { ok: false, reason: 'capability_unknown' };
  if (capability.status !== 'enabled') return { ok: false, reason: `capability_${capability.status}` as CaptureUnsupportedReason };
  if (!capability.allowed) return { ok: false, reason: 'capability_not_allowed' };
  return { ok: true };
}

export function validCaptureConsent(consent: unknown, now: number): consent is LocalCaptureConsent {
  if (typeof consent !== 'object' || consent === null) return false;
  const c = consent as Record<string, unknown>;
  const keys = Object.keys(c);
  if (keys.length !== 4 || !['confirmed_by_user', 'other_parties_notified', 'notice_version', 'confirmed_at'].every(k => keys.includes(k))) return false;
  if (c.confirmed_by_user !== true || c.other_parties_notified !== true) return false;
  if (typeof c.notice_version !== 'string' || !/^[A-Za-z0-9._-]{1,100}$/.test(c.notice_version)) return false;
  if (typeof c.confirmed_at !== 'string') return false;
  const at = Date.parse(c.confirmed_at);
  return Number.isFinite(at) && at <= now + 60_000 && now - at <= CONSENT_MAX_AGE_MS;
}

export interface CaptureLimits {
  readonly maxBytes: number;
  readonly maxDurationMs: number;
  readonly timesliceMs: number;
}

export const DEFAULT_CAPTURE_LIMITS: CaptureLimits = Object.freeze({ maxBytes: 64 * 1024 * 1024, maxDurationMs: 2 * 3600_000, timesliceMs: 5000 });

/** Engine-owned session; the UI receives it as a `LocalCaptureHandle`. */
export class LocalCaptureSession implements LocalCaptureHandle {
  private recorder: CaptureRecorder | null = null;
  private readonly listeners = new Set<(data: Blob, sequence: number) => void>();
  private readonly reasons = new Set<CaptureIncompleteReason>(['not_system_of_record', 'started_after_answer']);
  private readonly intervals: Array<{ kind: CaptureInterval['kind']; start: number; end: number | null }> = [];
  private bytes = 0;
  private chunks = 0;
  private participantChanges = 0;
  private inputSwitches = 0;
  private endedAt: number | null = null;
  private stoppedReason: string | null = null;
  private stopping: Promise<CaptureManifest> | null = null;
  private readonly startedAt: number;
  private durationTimer: unknown = null;
  private mimeType = 'unknown';

  constructor(
    readonly call_id: string,
    private readonly consent: LocalCaptureConsent,
    private readonly scheduler: Scheduler,
    private readonly limits: CaptureLimits,
    private readonly onStopped: (session: LocalCaptureSession) => void,
  ) {
    this.startedAt = scheduler.now();
  }

  get state(): 'recording' | 'stopped' { return this.endedAt === null ? 'recording' : 'stopped'; }

  attach(recorder: CaptureRecorder): void {
    this.recorder = recorder;
    this.mimeType = recorder.mimeType;
    this.durationTimer = this.scheduler.setTimeout(() => { this.reasons.add('duration_limit_reached'); void this.stop('duration_limit_reached'); }, this.limits.maxDurationMs);
  }

  push(data: Blob): void {
    if (this.endedAt !== null) return;
    this.bytes += data.size;
    this.chunks += 1;
    for (const listener of [...this.listeners]) { try { listener(data, this.chunks); } catch { /* listener isolated */ } }
    if (this.bytes >= this.limits.maxBytes) { this.reasons.add('size_limit_reached'); void this.stop('size_limit_reached'); }
  }

  recorderError(): void { this.reasons.add('recorder_error'); void this.stop('recorder_error'); }

  onData(listener: (data: Blob, sequence: number) => void): Disposer {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** Marks the start or end of a muted / held / media-lost interval. */
  mark(kind: CaptureInterval['kind'], active: boolean): void {
    if (this.endedAt !== null) return;
    const open = this.intervals.find(i => i.kind === kind && i.end === null);
    if (active && !open) {
      this.intervals.push({ kind, start: this.scheduler.now(), end: null });
      this.reasons.add(kind === 'muted' ? 'muted_intervals' : kind === 'held' ? 'held_intervals' : 'media_lost_intervals');
    } else if (!active && open) {
      open.end = this.scheduler.now();
    }
  }

  participantsChanged(): void { if (this.endedAt === null) { this.participantChanges += 1; this.reasons.add('participants_changed'); } }

  replaceLocal(stream: MediaStreamLike): void {
    if (this.endedAt !== null) return;
    this.inputSwitches += 1;
    this.reasons.add('input_device_switched');
    this.recorder?.replaceLocal(stream);
  }

  stop(reason = 'stopped_by_user'): Promise<CaptureManifest> {
    if (this.stopping) return this.stopping;
    this.stopping = (async () => {
      if (this.durationTimer !== null) { this.scheduler.clearTimeout(this.durationTimer); this.durationTimer = null; }
      try { await this.recorder?.stop(); } catch { this.reasons.add('recorder_error'); }
      const now = this.scheduler.now();
      for (const interval of this.intervals) if (interval.end === null) interval.end = now;
      this.endedAt = now;
      this.stoppedReason = reason;
      this.onStopped(this);
      return this.manifest();
    })();
    return this.stopping;
  }

  manifest(): CaptureManifest {
    const iso = (t: number) => new Date(t).toISOString();
    return Object.freeze({
      schema: 'bcio.local_capture_manifest',
      version: 1,
      call_id: this.call_id,
      authoritative: false,
      system_of_record: 'pbx',
      complete: false,
      scope: 'this_device_leg',
      channels: Object.freeze([
        Object.freeze({ index: 0, source: 'local_microphone_as_sent' }),
        Object.freeze({ index: 1, source: 'remote_audio_as_received', speakers: 'mixed_unattributed' }),
      ]) as CaptureManifest['channels'],
      mime_type: this.mimeType,
      persistence: 'caller_memory_only',
      encryption: 'none',
      upload: 'unsupported',
      consent: this.consent,
      started_at: iso(this.startedAt),
      ended_at: this.endedAt === null ? null : iso(this.endedAt),
      bytes: this.bytes,
      chunks: this.chunks,
      incomplete_reasons: Object.freeze([...this.reasons]),
      intervals: Object.freeze(this.intervals.map(i => Object.freeze({ kind: i.kind, start: iso(i.start), end: i.end === null ? null : iso(i.end) }))),
      participant_changes: this.participantChanges,
      input_switches: this.inputSwitches,
      stopped_reason: this.stoppedReason,
    });
  }
}
